import {
  DownloadIcon,
  Heart,
  Loader2,
  Plus,
  FileImage,
  Palette,
  FileBarChart2,
  Award,
  GraduationCap,
  BookOpen,
  LandPlot,
  UserCircle,
  Tag,
  Flag,
  Image,
  Wand2,
  ArrowLeft,
} from "lucide-react";
import {
  createImageGenerator,
  enhancePrompt,
  likeImageGenerator,
  unlikeImageGenerator,
  useGetImageGenerator,
  useGetTopImageGenerators,
} from "@packages/api/madlenAPI";
import {
  getEnvironment,
  joyrideStyles,
  joyrideStylesDark,
} from "@packages/utils";
import { CustomPromptInput } from "./CustomPromptInput";
import { useEffect, useState, useRef, useMemo } from "react";
import {
  ImageGeneratorResponseDto,
  WordAlternatives,
} from "@packages/api/madlenAPI.schemas";

import { useTranslation } from "react-i18next";
import FeaturedCarousel from "../tool_components/FeaturedCarousel";
import Joyride, { CallBackProps, Step } from "react-joyride";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { useMobile } from "@packages/hooks/useMobile";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { useToast } from "@packages/contexts/ToastContext";
import { Button, Text, TextLinear } from "@core/index";
import { ImageGeneratorCreateRequestDtoGenerationType } from "@packages/api/madlenAPI.schemas";
import { updateLastUsedTools } from "@packages/store/userSlice";
import { useAppDispatch } from "@packages/store/hooks";
import { useTheme } from "@packages/hooks/useTheme";

// Map for image generator types
export const IMAGE_GENERATOR_TYPES = {
  DEFAULT: {
    label: "image_generation_types.default",
    description: "image_generation_types.default_description",
    icon: Image,
    color: "indigo",
  },
  POSTER: {
    label: "image_generation_types.poster",
    description: "image_generation_types.poster_description",
    icon: FileImage,
    color: "blue",
  },
  LOGO: {
    label: "image_generation_types.logo",
    description: "image_generation_types.logo_description",
    icon: Palette,
    color: "emerald",
  },
  INFOGRAPHIC: {
    label: "image_generation_types.infographic",
    description: "image_generation_types.infographic_description",
    icon: FileBarChart2,
    color: "cyan",
  },
  BADGE: {
    label: "image_generation_types.badge",
    description: "image_generation_types.badge_description",
    icon: Award,
    color: "amber",
  },
  CERTIFICATE: {
    label: "image_generation_types.certificate",
    description: "image_generation_types.certificate_description",
    icon: GraduationCap,
    color: "purple",
  },
  FLASHCARD: {
    label: "image_generation_types.flashcard",
    description: "image_generation_types.flashcard_description",
    icon: BookOpen,
    color: "pink",
  },
  SCENE: {
    label: "image_generation_types.scene",
    description: "image_generation_types.scene_description",
    icon: LandPlot,
    color: "green",
  },
  AVATAR: {
    label: "image_generation_types.avatar",
    description: "image_generation_types.avatar_description",
    icon: UserCircle,
    color: "orange",
  },
  LABEL: {
    label: "image_generation_types.label",
    description: "image_generation_types.label_description",
    icon: Tag,
    color: "rose",
  },
  BANNER: {
    label: "image_generation_types.banner",
    description: "image_generation_types.banner_description",
    icon: Flag,
    color: "red",
  },
} as const;

export type ImageGeneratorImageType = keyof typeof IMAGE_GENERATOR_TYPES;

const convertToHighlightedWord = (wordAlt: WordAlternatives) => ({
  word: wordAlt.word || "",
  alternatives: wordAlt.alternatives || [],
});

const CreateImage = ({
  refetch,
  setSelectedImage,
  isGeneratedBefore,
}: {
  refetch: () => void;
  setSelectedImage: (image: ImageGeneratorResponseDto) => void;
  isGeneratedBefore: boolean;
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [newId, setNewId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageLikeLoading, setImageLikeLoading] = useState(-1);
  const [runTour, setRunTour] = useState(false);
  const [skipAllTours] = useLocalStorage("skip-all-tours", false);
  const isMobile = useMobile();
  const [highlightedWords, setHighlightedWords] = useState<WordAlternatives[]>(
    [],
  );
  const { addToast } = useToast();
  const dispatch = useAppDispatch();
  const [selectedTemplateType, setSelectedTemplateType] =
    useState<ImageGeneratorImageType>("DEFAULT");
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const { theme } = useTheme();

  const steps: Step[] = [
    {
      target: "body",
      content: t("onboarding.imagen.tour.welcome"),
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".featured-carousel",
      content: t("onboarding.imagen.tour.liked"),
      placement: "bottom",
    },
    {
      target: ".imagen-prompt-input",
      content: t("onboarding.imagen.tour.create"),
      placement: "bottom",
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action } = data;

    if (action === "close") {
      setRunTour(false);
      return;
    }

    if (status === "finished" || status === "skipped") {
      setRunTour(false);
    }
  };

  useEffect(() => {
    const shouldShowTour =
      !isGenerating &&
      !newId &&
      !isGeneratedBefore &&
      !skipAllTours &&
      !isMobile;
    setRunTour(shouldShowTour);
  }, [isGenerating, newId, isGeneratedBefore, skipAllTours, isMobile]);

  const { data: generatedImages } = useGetImageGenerator(newId!, {
    query: {
      enabled: !!newId,
      refetchInterval: (query) => {
        const exist =
          query.state.data?.images &&
          query.state.data?.images[0] &&
          query.state.data?.images[0].imageUrl;
        if (!exist) {
          return 3000;
        }
        return false;
      },
    },
  });

  const { data: topHooks, refetch: refetchTop } = useGetTopImageGenerators({
    size: 10,
  });

  useEffect(() => {
    if (
      generatedImages?.images &&
      generatedImages.images[0] &&
      generatedImages.images[0].wordAlternatives
    ) {
      setHighlightedWords(generatedImages.images[0].wordAlternatives);
    }
  }, [generatedImages]);

  useEffect(() => {
    if (generatedImages?.images && generatedImages.images[0]?.imageUrl) {
      setIsGenerating(false);
      refetch();
    }
  }, [generatedImages, refetch]);

  const { trackEvent: track } = trackEvent();
  const handleGenerateImages = async () => {
    try {
      setIsGenerating(true);
      const response = await createImageGenerator({
        prompt: prompt,
        isPublic: true,
        environment: getEnvironment(),
        generationType: ImageGeneratorCreateRequestDtoGenerationType.PROMPT,
        imageType: selectedTemplateType,
      });
      dispatch(updateLastUsedTools("images"));
      setNewId(response.id!);
    } catch (error: any) {
      setIsGenerating(false);
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      }
      console.error("Error generating images:", error);
    }

    track(
      {
        category: "ContentGeneration",
        action: "imagegen.generate",
        label: "Imagegen generation click",
        nonInteraction: false,
      },
      {
        location: "/imagegen/generate",
        element: "generate-button",
        imagegenPrompt: prompt,
      },
    );
  };

  const handleEnhanceClick = async () => {
    if (!prompt.trim()) return;

    if (isEnhanced) {
      setPrompt(originalPrompt);
      setIsEnhanced(false);
      return;
    }

    setIsEnhancing(true);
    setOriginalPrompt(prompt);

    const enhancedPromptRequest = await enhancePrompt({
      prompt: prompt,
      toolType: "IMAGE_GENERATOR",
      imageType: selectedTemplateType,
    });

    const enhancedPrompt = enhancedPromptRequest.result;
    if (enhancedPrompt) {
      setPrompt(enhancedPrompt);
    }

    setIsEnhancing(false);
    setIsEnhanced(true);
  };

  // Calculate optimal grid columns based on image count
  const gridColumns = useMemo(() => {
    if (selectedTemplateType === "DEFAULT" && isGenerating) {
      return "grid-cols-1 sm:grid-cols-2 md:grid-cols-2";
    }
    if (!generatedImages?.images) return "grid-cols-1";

    const imageCount = generatedImages.images.length;
    return imageCount === 1
      ? "grid-cols-1"
      : "grid-cols-1 sm:grid-cols-2 md:grid-cols-2";
  }, [generatedImages?.images, selectedTemplateType, isGenerating]);

  // Helper functions for styling based on selected template type color
  const getButtonBgColor = (color: string) => {
    const colorMap: Record<string, string> = {
      indigo: "bg-indigo-600",
      blue: "bg-blue-600",
      emerald: "bg-emerald-600",
      cyan: "bg-cyan-600",
      amber: "bg-amber-600",
      purple: "bg-purple-600",
      pink: "bg-pink-600",
      green: "bg-green-600",
      orange: "bg-orange-600",
      rose: "bg-rose-600",
      red: "bg-red-600",
    };
    return colorMap[color] || colorMap.indigo;
  };

  const getButtonShadow = (color: string) => {
    const colorMap: Record<string, string> = {
      indigo: "hover:shadow-indigo-600/20",
      blue: "hover:shadow-blue-600/20",
      emerald: "hover:shadow-emerald-600/20",
      cyan: "hover:shadow-cyan-600/20",
      amber: "hover:shadow-amber-600/20",
      purple: "hover:shadow-purple-600/20",
      pink: "hover:shadow-pink-600/20",
      green: "hover:shadow-green-600/20",
      orange: "hover:shadow-orange-600/20",
      rose: "hover:shadow-rose-600/20",
      red: "hover:shadow-red-600/20",
    };
    return colorMap[color] || colorMap.indigo;
  };

  const getButtonGradient = (color: string) => {
    const colorMap: Record<string, string> = {
      indigo: "from-indigo-600 to-purple-600",
      blue: "from-blue-600 to-cyan-600",
      emerald: "from-emerald-600 to-teal-600",
      cyan: "from-cyan-600 to-teal-600",
      amber: "from-amber-600 to-yellow-600",
      purple: "from-purple-600 to-pink-600",
      pink: "from-pink-600 to-rose-600",
      green: "from-green-600 to-lime-600",
      orange: "from-orange-600 to-amber-600",
      rose: "from-rose-600 to-pink-600",
      red: "from-red-600 to-rose-600",
    };
    return colorMap[color] || colorMap.indigo;
  };

  return (
    <>
      <Joyride
        locale={{
          back: t("onboarding.back"),
          close: t("onboarding.close"),
          last: t("onboarding.finish"),
          next: t("onboarding.next"),
          skip: t("onboarding.skip"),
        }}
        steps={steps}
        run={!skipAllTours && !isMobile && runTour}
        continuous
        showSkipButton
        styles={theme === "dark" ? joyrideStylesDark : joyrideStyles}
        callback={handleJoyrideCallback}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Left Column - Prompt Input */}
        <div className="bg-white dark:bg-dark-600 rounded-xl p-6 shadow-sm border border-dark-200 dark:border-dark-400 w-full transform transition-all duration-300 hover:shadow-lg h-fit imagen-prompt-input">
          <div className="space-y-6">
            {/* Prompt Input Section */}
            <div>
              <TextLinear
                from="from-blue-500 dark:from-blue-400"
                via="via-purple-500 dark:via-purple-400"
                to="to-pink-500 dark:to-pink-400"
                size={"2xl"}
                weight={"bold"}
                className="md:text-3xl animate-gradient-x"
              >
                imagen.enterPrompt
              </TextLinear>
              <Text
                variant={"p"}
                className="text-gray-600 dark:text-gray-400 md:text-base mt-2 leading-relaxed"
              >
                imagen.promptHelper
              </Text>
              <div className="mt-3 relative">
                <CustomPromptInput
                  value={prompt}
                  onChange={setPrompt}
                  highlightedWords={highlightedWords.map(
                    convertToHighlightedWord,
                  )}
                  placeholder={t("imagen.promptPlaceholder")}
                  disabled={isGenerating}
                  maxLength={1200}
                  dataTestId="imagen-prompt-input"
                  isEnhancing={isEnhancing}
                />
                <div
                  className="absolute z-[999] flex items-center gap-2 -bottom-4 right-28 text-sm bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 px-4 py-1.5 rounded-full text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
                  onClick={handleEnhanceClick}
                >
                  {isEnhanced ? (
                    <>
                      <ArrowLeft
                        className={`w-4 h-4 ${isEnhancing ? "animate-spin" : ""}`}
                      />
                      <span className="font-medium">{t("imagen.revert")}</span>
                    </>
                  ) : (
                    <>
                      <Wand2
                        className={`w-4 h-4 ${isEnhancing ? "animate-spin" : "animate-pulse"}`}
                      />
                      <span className="font-medium">{t("imagen.enhance")}</span>
                    </>
                  )}
                </div>
                <div className="absolute z-[999] -bottom-4 border border-gray-200 dark:border-gray-600 right-4 text-sm bg-gray-100 dark:bg-dark-500 px-4 py-1.5 rounded-full text-gray-500 dark:text-gray-400 shadow-lg">
                  {prompt.length}/1200
                </div>
              </div>
            </div>

            {/* Template Selection Section */}
            <div className="relative">
              <TextLinear
                from="from-indigo-500 dark:from-indigo-400"
                via="via-purple-500 dark:via-purple-400"
                to="to-pink-500 dark:to-pink-400"
                size={"lg"}
                weight={"bold"}
                className="animate-gradient-x"
              >
                {t("imagen.selectTemplate")} ({t("common.optional")})
              </TextLinear>

              <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-1.5">
                {Object.entries(IMAGE_GENERATOR_TYPES).map(
                  ([type, { label, icon: IconComponent, color }]) => {
                    const isSelected = selectedTemplateType === type;

                    // Helper function to get color classes
                    const getColorClasses = (colorName: string) => {
                      const colorMap: Record<
                        string,
                        { bg: string; text: string; ring: string }
                      > = {
                        indigo: {
                          bg: "bg-indigo-100 dark:bg-indigo-900/30",
                          text: "text-indigo-600 dark:text-indigo-300",
                          ring: "ring-1 ring-indigo-500 dark:ring-indigo-400",
                        },
                        blue: {
                          bg: "bg-blue-100 dark:bg-blue-900/30",
                          text: "text-blue-600 dark:text-blue-300",
                          ring: "ring-1 ring-blue-500 dark:ring-blue-400",
                        },
                        emerald: {
                          bg: "bg-emerald-100 dark:bg-emerald-900/30",
                          text: "text-emerald-600 dark:text-emerald-300",
                          ring: "ring-1 ring-emerald-500 dark:ring-emerald-400",
                        },
                        cyan: {
                          bg: "bg-cyan-100 dark:bg-cyan-900/30",
                          text: "text-cyan-600 dark:text-cyan-300",
                          ring: "ring-1 ring-cyan-500 dark:ring-cyan-400",
                        },
                        amber: {
                          bg: "bg-amber-100 dark:bg-amber-900/30",
                          text: "text-amber-600 dark:text-amber-300",
                          ring: "ring-1 ring-amber-500 dark:ring-amber-400",
                        },
                        purple: {
                          bg: "bg-purple-100 dark:bg-purple-900/30",
                          text: "text-purple-600 dark:text-purple-300",
                          ring: "ring-1 ring-purple-500 dark:ring-purple-400",
                        },
                        pink: {
                          bg: "bg-pink-100 dark:bg-pink-900/30",
                          text: "text-pink-600 dark:text-pink-300",
                          ring: "ring-1 ring-pink-500 dark:ring-pink-400",
                        },
                        green: {
                          bg: "bg-green-100 dark:bg-green-900/30",
                          text: "text-green-600 dark:text-green-300",
                          ring: "ring-1 ring-green-500 dark:ring-green-400",
                        },
                        orange: {
                          bg: "bg-orange-100 dark:bg-orange-900/30",
                          text: "text-orange-600 dark:text-orange-300",
                          ring: "ring-1 ring-orange-500 dark:ring-orange-400",
                        },
                        rose: {
                          bg: "bg-rose-100 dark:bg-rose-900/30",
                          text: "text-rose-600 dark:text-rose-300",
                          ring: "ring-1 ring-rose-500 dark:ring-rose-400",
                        },
                        red: {
                          bg: "bg-red-100 dark:bg-red-900/30",
                          text: "text-red-600 dark:text-red-300",
                          ring: "ring-1 ring-red-500 dark:ring-red-400",
                        },
                      };

                      return colorMap[colorName] || colorMap.indigo;
                    };

                    const colorClasses = getColorClasses(color);

                    return (
                      <div
                        key={type}
                        className={`px-1.5 py-1 cursor-pointer rounded-lg transition-all duration-200 flex flex-col items-center justify-center ${
                          isSelected
                            ? `${colorClasses.bg} ${colorClasses.ring}`
                            : "bg-white dark:bg-dark-500 border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:scale-105"
                        } ${type === "DEFAULT" ? "col-span-2" : ""}`}
                        onClick={() =>
                          setSelectedTemplateType(
                            type as ImageGeneratorImageType,
                          )
                        }
                        role="option"
                        aria-selected={isSelected}
                        title={t(
                          IMAGE_GENERATOR_TYPES[type as ImageGeneratorImageType]
                            .description,
                        )}
                      >
                        <IconComponent
                          size={16}
                          className={
                            isSelected
                              ? colorClasses.text
                              : "text-gray-600 dark:text-gray-400"
                          }
                        />
                        <span
                          className={`text-xs mt-1 text-center line-clamp-1 w-full ${isSelected ? colorClasses.text : "text-gray-800 dark:text-gray-200"}`}
                        >
                          {t(label)}
                        </span>
                      </div>
                    );
                  },
                )}
              </div>
            </div>

            <Button
              variant="solid"
              size="lg"
              rounded="xl"
              fullWidth
              disabled={isGenerating || !prompt.trim()}
              textColor="text-white"
              bgColor={
                isGenerating
                  ? "bg-purple-400/80 dark:bg-purple-400/80"
                  : getButtonBgColor(
                      IMAGE_GENERATOR_TYPES[selectedTemplateType].color,
                    )
              }
              className={`disabled:opacity-100 relative overflow-hidden group hover:shadow-lg mt-2 ${getButtonShadow(IMAGE_GENERATOR_TYPES[selectedTemplateType].color)}`}
              onClick={handleGenerateImages}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-r ${getButtonGradient(IMAGE_GENERATOR_TYPES[selectedTemplateType].color)} transition-transform duration-300
      ${isGenerating ? "translate-x-0" : "-translate-x-full"}`}
              />

              <div
                className="relative flex items-center justify-center gap-2"
                data-testid="generate-images-button"
              >
                {isGenerating ? (
                  <>
                    <div className="w-5 h-5 relative">
                      <div className="absolute inset-0 rounded-full border-2 border-white border-opacity-20" />
                      <div className="absolute inset-0 rounded-full border-2 border-white border-l-transparent animate-spin" />
                    </div>
                    {t("imagen.generating")}
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    {t("imagen.generate")}
                  </>
                )}
              </div>
            </Button>
          </div>
        </div>

        {/* Right Column - Generated Images */}
        {generatedImages?.images || isGenerating ? (
          <div
            className="bg-white dark:bg-dark-600 rounded-xl p-6 shadow-sm border border-dark-200 dark:border-dark-400"
            data-testid="images-generated-container"
          >
            <div className="space-y-6">
              <Text variant={"h1"}>imagen.generatedImages</Text>

              {/* Generated Images Grid */}
              <div
                className={`grid gap-4 ${gridColumns} ${!isGenerating ? "justify-items-center" : ""}`}
              >
                {isGenerating
                  ? Array.from({
                      length: selectedTemplateType === "DEFAULT" ? 4 : 1, // Always show 4 loading skeletons
                    }).map((_, index) => (
                      <div
                        key={index}
                        className="relative aspect-square rounded-xl overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700" />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine-wave" />
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-black/5 dark:from-white/5 dark:to-black/20" />
                      </div>
                    ))
                  : generatedImages?.images
                    ? generatedImages.images.map((image, index) => {
                        // Handle aspect ratio from image data or use default
                        // Convert string aspect ratio from the API or fallback to 1:1
                        const aspectRatio =
                          typeof image.aspectRatio === "string"
                            ? image.aspectRatio
                            : "1:1";
                        const aspectRatioClass = (() => {
                          switch (aspectRatio) {
                            case "16:9":
                              return "aspect-video"; // 16:9
                            case "9:16":
                              return "aspect-[9/16]"; // 9:16
                            case "4:3":
                              return "aspect-[4/3]"; // 4:3
                            case "3:4":
                              return "aspect-[3/4]"; // 3:4
                            case "3:2":
                              return "aspect-[3/2]"; // 3:2
                            case "2:3":
                              return "aspect-[2/3]"; // 2:3
                            case "1:1":
                            default:
                              return "aspect-square"; // 1:1
                          }
                        })();

                        return (
                          <div
                            key={index}
                            onClick={() => setSelectedImage(generatedImages)}
                            className={`${aspectRatioClass} rounded-xl overflow-hidden relative group cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl max-w-full w-full mx-auto max-h-[500px] border border-gray-200 dark:border-dark-400
  `}
                          >
                            <img
                              src={image.imageUrl}
                              alt={`Generated image ${index + 1}`}
                              className={`w-full h-full ${
                                selectedTemplateType === "DEFAULT"
                                  ? "object-cover"
                                  : "object-contain"
                              }  transition-transform duration-500 group-hover:scale-110`}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                            <Button
                              variant="solid"
                              size="icon"
                              rounded="lg"
                              bgColor="bg-black/60 hover:bg-black/80 dark:bg-black/60 dark:hover:bg-black/80"
                              textColor="text-white"
                              className="absolute bottom-3 right-3 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:scale-110 active:scale-95 transition-all duration-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = document.createElement("a");
                                link.href = image.imageUrl || "";
                                link.download = `generated-image-${index + 1}.png`;
                                link.click();
                              }}
                            >
                              <DownloadIcon className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })
                    : null}
              </div>
            </div>
          </div>
        ) : (
          <div className="featured-carousel">
            <FeaturedCarousel
              dataLength={topHooks?.data?.length || 0}
              backgroundColor="bg-white dark:bg-dark-600 shadow-sm border border-dark-200 dark:border-dark-400"
              gradientColor="from-indigo-500 to-purple-500"
              renderFeaturedCards={() => {
                return topHooks?.data?.map((image) => (
                  <div
                    key={image.id}
                    className="flex-none w-56 cursor-pointer hover:scale-105 transition-all duration-300 bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 overflow-hidden"
                    onClick={() => {
                      setSelectedImage(image);
                    }}
                  >
                    <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm overflow-hidden">
                      {image.images && image.images[0] && (
                        <div className="relative aspect-square">
                          <img
                            src={image.images[0].imageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          <Button
                            variant="solid"
                            size="xs"
                            rounded="full"
                            className="absolute bottom-3 right-3"
                            bgColor={
                              image.isLiked
                                ? "bg-red-100 dark:bg-red-900/30"
                                : "bg-gray-100 dark:bg-gray-700"
                            }
                            textColor={
                              image.isLiked
                                ? "text-red-600 dark:text-red-400"
                                : "text-gray-600 dark:text-gray-400"
                            }
                            leftIcon={
                              imageLikeLoading === image.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Heart size={14} />
                              )
                            }
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (image.id) {
                                if (image.isLiked) {
                                  setImageLikeLoading(image.id);
                                  await unlikeImageGenerator(image.id);
                                  setImageLikeLoading(-1);
                                } else {
                                  setImageLikeLoading(image.id);
                                  await likeImageGenerator(image.id);
                                  setImageLikeLoading(-1);
                                }
                                refetchTop();
                              }
                            }}
                          >
                            <span>{image.likeCount}</span>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ));
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CreateImage;
